import { House, CalendarDays, MessagesSquare, CircleUserRound, Gauge, ClipboardList, BrainCircuit } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { memo } from "react";

type ViewId = string;

interface BottomNavProps {
  currentView: ViewId;
  onNavigate: (view: ViewId) => void;
  userRole: "consumer" | "provider";
}

const consumerTabs = [
  { id: "home", label: "Home", icon: House },
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "inbox", label: "Inbox", icon: MessagesSquare },
  { id: "profile", label: "Profile", icon: CircleUserRound },
];

const providerTabs = [
  { id: "hub", label: "Hub", icon: Gauge },
  { id: "jobs", label: "Jobs", icon: ClipboardList },
  { id: "inbox", label: "Inbox", icon: MessagesSquare },
  { id: "ai", label: "AI", icon: BrainCircuit },
];

const BottomNav = memo(({ currentView, onNavigate, userRole }: BottomNavProps) => {
  const { user } = useAuth();
  const unreadCount = useUnreadMessages(user?.id, userRole === "provider");
  const tabs = userRole === "consumer" ? consumerTabs : providerTabs;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] h-[76px] pointer-events-none flex justify-center">
      <nav className="glass-nav w-full max-w-md mx-auto rounded-t-[24px] flex items-center px-4 pb-2 pt-1 pointer-events-auto h-full">
        {tabs.map((tab) => {
          const isActive = currentView === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onNavigate(tab.id)}
              className={`flex-1 flex flex-col items-center justify-center h-full transition-all duration-250 press-scale relative group`}
            >
              {isActive && (
                <div className="absolute inset-x-2 inset-y-1.5 rounded-2xl bg-emerald-accent/10 animate-in fade-in zoom-in-95 duration-500" />
              )}

              <div className={`relative transition-all duration-200 ${isActive ? "scale-110 -translate-y-0.5" : "group-hover:translate-y-[-2px]"}`}>
                <tab.icon
                  size={26}
                  strokeWidth={isActive ? 2.5 : 2}
                  className={`transition-colors duration-200 relative z-10 ${isActive ? "text-emerald-accent drop-shadow-[0_0_8px_rgba(79,175,138,0.5)]" : "text-brand-text-muted/70 group-hover:text-brand-text-muted dark:text-foreground/40 dark:group-hover:text-foreground/60"}`}
                />
                {tab.id === "inbox" && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 bg-emerald-500 text-white text-[9px] font-black min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full border border-emerald-500/20 dark:border-white/10 shadow-[0_0_12px_rgba(16,185,129,0.5)] animate-in zoom-in duration-300 z-20 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-shimmer scale-150 rotate-[35deg]" />
                    <span className="relative flex h-1.5 w-1.5 mr-0.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                    </span>
                    <span className="relative z-10">{unreadCount > 99 ? "99+" : unreadCount}</span>
                  </span>
                )}
              </div>

              <span className={`text-[10px] mt-1 tracking-widest font-bold uppercase transition-all duration-200 relative z-10 ${isActive ? "text-emerald-accent opacity-100" : "text-brand-text-muted/80 group-hover:text-brand-text-primary dark:text-foreground/40 dark:opacity-60 dark:group-hover:opacity-100"}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
});

BottomNav.displayName = "BottomNav";

export default BottomNav;
